// client/src/pages/ConversationView.js
import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ConversationView.css';
import {
  getMessagesByConversation,
  sendMessageAsAgent
} from '../services/messageService';
import {
  getConversationById,
  markConversationAsAutomated,
  updateConversationDetails,
  closeConversation,
  reopenConversation,
  reassignConversation,
  deleteConversation
} from '../services/conversationService';
import { blockContact } from '../services/blockedContactService';
import { getTemplatesByConversation } from '../services/templateService';
import { listNotes, createNote } from '../services/noteService';
import { getSocket, onBothSockets, getSockets } from '../services/socketService';
import { sendOtpToClient, getLeadStatus, sendLeadDocument } from '../services/leadService';
import { getUserIdFromToken, getUserRoleFromToken } from '../utils/auth';
import TemplateSelector from '../components/TemplateSelector';
import QuickReplySelector from '../components/QuickReplySelector';
import QuickReplyPicker from '../components/QuickReplyPicker';
import DocumentUploader from '../components/DocumentUploader';
import AudioRecorder from '../components/AudioRecorder';
import TemplatePicker from '../components/TemplatePicker';
import ReassignModal from '../components/ReassignModal';
import BlockContactModal from '../components/BlockContactModal';
import { MessageImage, MessageVideo, MessageAudio, MessageDocumentLink } from '../components/MessageMedia';
import { releaseConversationMedia } from '../services/mediaService';

export default function ConversationView({ conversationId: conversationIdProp }) {
  const { t } = useTranslation();

  const { id: routeId } = useParams();
  const conversationId = conversationIdProp ?? routeId;
  const navigate = useNavigate();

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingConv, setLoadingConv] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  // Notas y modal
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('list'); // 'list' | 'create'
  const [notes, setNotes] = useState([]);
  const [newNoteContent, setNewNoteContent] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  // Lead details
  const [leadId, setLeadId] = useState('');
  const [email, setEmail] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  // Lead status from Kitt
  const [leadStatus, setLeadStatus] = useState(null);
  const [loadingLeadStatus, setLoadingLeadStatus] = useState(false);
  const [leadStatusError, setLeadStatusError] = useState('');
  // Templates (shared between TemplateSelector and TemplatePicker)
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  // Document sending states
  const [sendingContract, setSendingContract] = useState(false);
  const [sendingForecast, setSendingForecast] = useState(false);
  const [sendingPromo, setSendingPromo] = useState(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptionsMenu && !event.target.closest('[data-options-menu]')) {
        setShowOptionsMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showOptionsMenu]);

  // Check if conversation is inactive for 24 hours (WhatsApp restriction)
  const isConversationInactive = () => {
    if (!conversation?.lastMessageAt) return false;
    const lastMessageTime = new Date(conversation.lastMessageAt).getTime();
    const currentTime = new Date().getTime();
    const hoursDiff = (currentTime - lastMessageTime) / (1000 * 60 * 60);
    return hoursDiff >= 24;
  };

  // Check if conversation has messages
  const hasMessages = () => {
    return messages.length > 0;
  };

  // Check if user can send messages (must have messages AND not be inactive)
  const canSendMessage = () => {
    return hasMessages() && !isConversationInactive() && canUserInteract();
  };

  // Check if user can interact with this conversation
  const canUserInteract = () => {
    if (!conversation) return false;

    const userRole = getUserRoleFromToken();
    const userId = getUserIdFromToken();

    // Manager and Admin can always interact
    if (userRole === 'manager' || userRole === 'admin') {
      return true;
    }

    // If conversation has no owner (agentId), anyone can interact
    if (!conversation.agentId) {
      return true;
    }

    // If conversation has owner, only the owner can interact
    return conversation.agentId === userId;
  };

  // Check if user is admin (for delete functionality)
  const isUserAdmin = () => {
    const userRole = getUserRoleFromToken();
    return userRole === 'admin';
  };

  // 1) Cargar mensajes históricos
  async function fetchMessages() {
    try {
      const data = await getMessagesByConversation(conversationId);
      setMessages(data);
    } catch (err) {
      console.error('[ConversationView] Error cargando mensajes:', err);
      setError(t('conversation.message.error.load'));
    } finally {
      setLoading(false);
    }
  }

  // 2) Cargar detalles de conversación
  async function fetchConversationDetails() {
    try {
      const conv = await getConversationById(conversationId);
      setConversation(conv);
      // Populate leadId and email from conversation
      setLeadId(conv.leadId || '');
      setEmail(conv.email || '');
    } catch (err) {
      console.error('[ConversationView] Error cargando conversación:', err);
      setError(t('conversation.error.load'));
    } finally {
      setLoadingConv(false);
    }
  }

  // 2b) Cargar estado del lead desde Kitt
  async function fetchLeadStatus() {
    if (!conversation?.whatsappId) return;

    setLoadingLeadStatus(true);
    setLeadStatusError('');

    try {
      const response = await getLeadStatus(conversation.whatsappId, conversation.market);
      setLeadStatus(response.data);
      if (!conversation.email && response.data.email) {
        handleSaveDetails(response.data.email);
      }
    } catch (err) {
      console.error('[ConversationView] Error cargando estado del lead:', err);
      setLeadStatusError(err.message || 'Error al cargar datos del lead');
    } finally {
      setLoadingLeadStatus(false);
    }
  }

  // 3) Montaje inicial
  useEffect(() => {
    fetchConversationDetails();
    fetchMessages();
    // Al salir de la conversación se liberan los adjuntos que se bajaron para pintarla.
    return () => releaseConversationMedia(conversationId);
  }, [conversationId]);

  // 3b) Cargar estado del lead cuando la conversación esté disponible
  useEffect(() => {
    if (conversation?.whatsappId) {
      fetchLeadStatus();
    }
  }, [conversation?.whatsappId]);

  const fetchNotes = async () => {
    try {
      const data = await listNotes(conversationId);
      setNotes(data);
    } catch (err) {
      console.error('[ConversationView] Error cargando notas:', err);
      setError(t('conversation.note.error.load'));
    }
  }

  useEffect(() => {
    fetchNotes();
    const sockets = getSockets();

    const handleNoteAdded = (note) => {
      if (note.conversationId === conversationId) {
        setNotes(prev => [...prev, note]);
      }
    };

    onBothSockets("noteAdded", handleNoteAdded);

    return () => {
      sockets.it.off("noteAdded", handleNoteAdded);
      sockets.es.off("noteAdded", handleNoteAdded);
    };
  }, [conversationId]);

  // 5) Manejar creación de nueva nota
  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    try {
      await createNote(conversationId, newNoteContent);
      setNewNoteContent('');
      setModalMode('list');
    } catch (err) {
      console.error('[ConversationView] Error saving note:', err);
      setError(t('conversation.note.error.save'));
    }
  };

  // 6) Socket.io: unirse a sala y escuchar eventos en AMBOS backends
  useEffect(() => {
    const sockets = getSockets();

    // Unirse a la conversación en ambos backends
    if (sockets.it.connected) {
      sockets.it.emit('joinConversation', conversationId);
    } else {
      sockets.it.once('connect', () => sockets.it.emit('joinConversation', conversationId));
    }

    if (sockets.es.connected) {
      sockets.es.emit('joinConversation', conversationId);
    } else {
      sockets.es.once('connect', () => sockets.es.emit('joinConversation', conversationId));
    }

    const handleNewMessage = msg => setMessages(prev => [...prev, msg]);
    const handleStatusUpdated = ({ conversationId: cid, status }) => {
      if (cid === conversationId) setConversation(prev => prev && { ...prev, status });
    };
    const handleDetailsUpdated = ({ conversationId: cid, leadId: newLeadId, email: newEmail }) => {
      if (cid === conversationId) {
        setConversation(prev => prev && { ...prev, leadId: newLeadId, email: newEmail });
        setLeadId(newLeadId || '');
        setEmail(newEmail || '');
      }
    };
    const handleMessageStatusUpdate = ({ messageId, deliveryStatus }) => {
      console.log(`[ConversationView] 📊 Estado actualizado: ${messageId} -> ${deliveryStatus}`);
      setMessages(prev => prev.map(msg =>
        msg.id === messageId ? { ...msg, deliveryStatus } : msg
      ));
    };

    // Registrar listeners en ambos sockets
    onBothSockets('newMessage', handleNewMessage);
    onBothSockets('statusUpdated', handleStatusUpdated);
    onBothSockets('conversationDetailsUpdated', handleDetailsUpdated);
    onBothSockets('messageStatusUpdate', handleMessageStatusUpdate);

    return () => {
      // Salir de la conversación en ambos backends
      sockets.it.emit('leaveConversation', conversationId);
      sockets.es.emit('leaveConversation', conversationId);

      // Limpiar listeners de ambos sockets
      sockets.it.off('newMessage', handleNewMessage);
      sockets.it.off('statusUpdated', handleStatusUpdated);
      sockets.it.off('conversationDetailsUpdated', handleDetailsUpdated);
      sockets.it.off('messageStatusUpdate', handleMessageStatusUpdate);
      sockets.es.off('newMessage', handleNewMessage);
      sockets.es.off('statusUpdated', handleStatusUpdated);
      sockets.es.off('conversationDetailsUpdated', handleDetailsUpdated);
      sockets.es.off('messageStatusUpdate', handleMessageStatusUpdate);
    };
  }, [conversationId]);

  // Load templates (shared)
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const data = await getTemplatesByConversation(conversationId);
        setTemplates(data.templates || []);
      } catch (err) {
        console.error('[ConversationView] Error loading templates:', err);
      } finally {
        setTemplatesLoading(false);
      }
    };
    loadTemplates();
  }, [conversationId]);

  // 7) Scroll automático
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 8) Enviar mensaje
  const handleSend = async e => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await sendMessageAsAgent(conversationId, newMessage.trim());
      setNewMessage('');
    } catch (err) {
      console.error('[ConversationView] Error enviando mensaje:', err);
      setError(t('conversation.message.error.send'));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      if (newMessage.trim()) {
        e.preventDefault();
        handleSend(e);
      }
    }
  };


  // 9) Marcar como automática
  const handleAutomate = async () => {
    try {
      await markConversationAsAutomated(conversationId);
      fetchConversationDetails();
    } catch (err) {
      console.error('[ConversationView] Error al marcar como automática:', err);
      setError(t('conversation.error.automate'));
    }
  };

  const sendOTP = () => {
    try { sendOtpToClient(conversation.id); }
    catch (err) { console.error('[ConversationView] OTP error:', err); }
  };

  // Handler para cerrar conversación
  const handleCloseConversation = async () => {
    try {
      await closeConversation(conversationId);
      setSuccess('Conversación cerrada correctamente');
      fetchConversationDetails();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('[ConversationView] Error cerrando conversación:', err);
      setError('Error al cerrar conversación');
      setTimeout(() => setError(''), 2000);
    }
  };

  // Handler para reabrir conversación
  const handleReopenConversation = async () => {
    try {
      await reopenConversation(conversationId);
      setSuccess('Conversación reabierta correctamente');
      fetchConversationDetails();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('[ConversationView] Error reabriendo conversación:', err);
      setError('Error al reabrir conversación');
      setTimeout(() => setError(''), 2000);
    }
  };

  // Handler para eliminar conversación
  const handleDeleteConversation = async () => {
    try {
      await deleteConversation(conversationId);
      setSuccess('Conversación eliminada correctamente');
      setDeleteModalOpen(false);
      setTimeout(() => {
        navigate('/'); // Redirigir al dashboard después de eliminar
      }, 1000);
    } catch (err) {
      console.error('[ConversationView] Delete error:', err);
      setError('Error al eliminar conversación');
      setDeleteModalOpen(false);
      setTimeout(() => setError(''), 2000);
    }
  };

  // Handler para reasignar conversación
  const handleReassign = async (agentId) => {
    try {
      const result = await reassignConversation(conversationId, agentId);
      setSuccess(`Conversación reasignada a ${result.conversation.agentName}`);
      fetchConversationDetails();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('[ConversationView] Error reasignando conversación:', err);
      throw err; // Propagar el error para que el modal lo maneje
    }
  };

  // Handler para bloquear contacto
  const handleBlockContact = async (reason) => {
    try {
      await blockContact(conversation.whatsappId, reason, conversationId);
      setSuccess('Contacto bloqueado correctamente');
      setTimeout(() => {
        setSuccess('');
        navigate('/dashboard'); // Redirigir al dashboard después de bloquear
      }, 2000);
    } catch (err) {
      console.error('[ConversationView] Error bloqueando contacto:', err);
      throw err; // Propagar el error para que el modal lo maneje
    }
  };

  // Handler para guardar leadId y email
  const handleSaveDetails = async (emailParam = null) => {
    // Si se proporciona email por parámetro, usarlo; si no, usar el del estado
    const emailToSave = emailParam || email;

    if (!leadId.trim() && !emailToSave.trim()) {
      setDetailsError('Debe proporcionar al menos leadId o email');
      return;
    }

    setSavingDetails(true);
    setDetailsError('');

    try {
      const details = {};
      if (leadId.trim()) details.leadId = leadId.trim();
      if (emailToSave.trim()) details.email = emailToSave.trim();

      const data = await updateConversationDetails(conversationId, details);

      // Update conversation with new values
      setConversation(prev => ({
        ...prev,
        leadId: data.conversation.leadId,
        email: data.conversation.email
      }));

      // Si se guardó un email por parámetro, actualizar el estado también
      if (emailParam && emailParam.trim()) {
        setEmail(emailParam.trim());
      }

      setSuccess('Detalles actualizados correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('[ConversationView] Error guardando detalles:', err);
      setDetailsError(err.message || 'Error al guardar detalles');
    } finally {
      setSavingDetails(false);
    }
  };

  // Handler para cuando se selecciona una quick reply
  const handleQuickReplySelect = (content) => {
    setNewMessage(content);
    // Focus en el textarea
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  // Handler para enviar documentos al lead
  const handleSendDocument = async (documentType) => {
    const email = leadStatus?.email || conversation?.email;
    if (!email) {
      setError('No se encontró email del lead');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const setLoading = {
      contract: setSendingContract,
      forecast: setSendingForecast,
      'promo-conditions': setSendingPromo
    }[documentType];

    const documentNames = {
      contract: 'contrato',
      forecast: 'forecast',
      'promo-conditions': 'condiciones de promoción'
    };

    setLoading(true);
    try {
      await sendLeadDocument(documentType, email, conversation?.market, conversationId);
      setSuccess(`${documentNames[documentType].charAt(0).toUpperCase() + documentNames[documentType].slice(1)} enviado correctamente`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(`Error al enviar ${documentNames[documentType]}`);
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Condiciones para habilitar botones de documentos
  const canSendContract = () => {
    return canSendMessage() && leadStatus && leadStatus.status === 'complete';
  };

  const canSendForecast = () => {
    const excludedStatuses = ['telesubscription', 'claim', 'cancelled'];
    return canSendMessage() && leadStatus && !excludedStatuses.includes(leadStatus.status);
  };

  const canSendPromoConditions = () => {
    return canSendMessage() && leadStatus;
  };

  const modal = notesModalOpen && (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50" onClick={() => setNotesModalOpen(false)}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 relative" onClick={e => e.stopPropagation()}>
        <button onClick={() => setNotesModalOpen(false)} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800">✕</button>
        {modalMode === 'list' ? (
          <>
            <h2 className="text-2xl font-semibold mb-4">{t('conversation.note.title')}</h2>
            <ul className="space-y-3 max-h-64 overflow-auto mb-6">
              {notes.length ? notes.map(n => (
                <li key={n.id} className="p-3 bg-gray-50 rounded">
                  <div className="text-xs text-gray-500 mb-1">{n.author.name} • {new Date(n.createdAt).toLocaleString()}</div>
                  <div>{n.content}</div>
                </li>
              )) : <li className="text-gray-500">{t('conversation.note.empty')}</li>}
            </ul>
            <div className="flex justify-end">
              <button onClick={() => setModalMode('create')} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">{t('conversation.note.create')}</button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-semibold mb-4">{t('conversation.note.new')}</h2>
            <textarea rows={4} value={newNoteContent} onChange={e => setNewNoteContent(e.target.value)} className="w-full border rounded p-2 mb-4" placeholder={t('conversation.note.placeholder')} />
            <div className="flex justify-between">
              <button onClick={() => setModalMode('list')} className="px-4 py-2 rounded border hover:bg-gray-100">{t('common.back')}</button>
              <button onClick={handleAddNote} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-[#423ef4]">{t('conversation.note.save')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
  return (
    <>
      <div style={styles.wrapper}>
        {/* Navbar superior */}
        <div style={styles.topNavbar} className="conversation-navbar">
          <div style={styles.navbarLeft}>
            {!conversationIdProp && (
              <button style={styles.backBtn} onClick={() => navigate(-1)}>
                ← {t('common.back')}
              </button>
            )}
            <h2 style={styles.navbarTitle}>
              {conversation?.whatsappId || t('conversation.title')}
              {conversation?.status === 'agent_assigned' ? (
                <span style={styles.badgeAgent}>{t('conversation.mode.agent')}</span>
              ) : conversation?.status ? (
                <span style={styles.badgeAuto}>{t('conversation.mode.automatic')}</span>
              ) : null}
            </h2>
          </div>

          <div style={styles.navbarRight}>
            {/* Botón cerrar/reabrir conversación */}
            {conversation?.status === 'closed' ? (
              <button
                onClick={handleReopenConversation}
                style={styles.reopenBtn}
                disabled={!canUserInteract()}
              >
                ↻ Reabrir
              </button>
            ) : (
              <button
                onClick={handleCloseConversation}
                style={styles.closeBtn}
                disabled={!canUserInteract()}
              >
                ✓ Cerrar
              </button>
            )}

            {/* Menú de opciones (3 puntos) */}
            <div style={styles.optionsMenuContainer} data-options-menu>
              <button
                style={styles.optionsMenuBtn}
                onClick={() => setShowOptionsMenu(!showOptionsMenu)}
              >
                ⋮
              </button>

              {showOptionsMenu && (
                <div style={styles.optionsDropdown} data-options-menu>
                  <button
                    style={styles.optionItem}
                    onClick={() => {
                      setBlockModalOpen(true);
                      setShowOptionsMenu(false);
                    }}
                    disabled={!canUserInteract()}
                  >
                    🚫 Bloquear contacto
                  </button>
                  <button
                    style={styles.optionItem}
                    onClick={() => {
                      setReassignModalOpen(true);
                      setShowOptionsMenu(false);
                    }}
                    disabled={!canUserInteract()}
                  >
                    👤 Reasignar conversación
                  </button>
                  <button
                    style={styles.optionItem}
                    onClick={() => {
                      setNotesModalOpen(true);
                      setModalMode('list');
                      setShowOptionsMenu(false);
                    }}
                  >
                    📝 Ver notas
                  </button>
                  {isUserAdmin() && (
                    <button
                      style={{...styles.optionItem, color: '#dc2626'}}
                      onClick={() => {
                        setDeleteModalOpen(true);
                        setShowOptionsMenu(false);
                      }}
                    >
                      🗑️ Eliminar conversación
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contenedor principal con paneles izquierdo y derecho */}
        <div style={styles.mainContent} className="conversation-main-content">
          {/* Panel izquierdo: mensajes */}
          <div style={styles.leftPanel} className="conversation-left-panel">

          <div style={styles.messagesContainer}>
            {loading || loadingConv ? null : messages.length === 0 ? (
              <p style={styles.noMessagesText}>{t('conversation.message.empty')}</p>
            ) : (
              messages.map((msg) => {
                const isAgent = msg.from !== 'user';
                return (
                  <div
                    key={msg.id}
                    style={{
                      ...styles.messageBubble,
                      alignSelf: isAgent ? 'flex-end' : 'flex-start',
                      backgroundColor: isAgent ? '#daf1da' : '#f1f0f0',
                      border: isAgent ? '1px solid #4caf50' : '1px solid #ccc',
                    }}
                  >
                    <div style={styles.messageHeader}>
                      <span
                        style={{
                          ...styles.senderLabel,
                          backgroundColor: isAgent ? '#4caf50' : '#888',
                        }}
                      >
                        {isAgent ? msg.from : t('conversation.message.user')}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={styles.timestamp}>
                          {new Date(msg.timestamp).toLocaleString()}
                        </span>
                        {/* Iconos de estado solo para mensajes del agente */}
                        {isAgent && msg.deliveryStatus && (
                          <span style={{ fontSize: '0.75rem', color: '#666' }}>
                            {msg.deliveryStatus === 'sent' && '✓'}
                            {msg.deliveryStatus === 'delivered' && '✓✓'}
                            {msg.deliveryStatus === 'read' && <span style={{ color: '#34B7F1' }}>✓✓</span>}
                            {msg.deliveryStatus === 'failed' && '❌'}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Renderizar según el tipo de contenido */}
                    {msg.mediaType === 'image' && msg.mediaFilename ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <MessageImage
                          conversationId={conversationId}
                          msg={msg}
                          alt="Imagen"
                          style={{
                            maxWidth: '300px',
                            maxHeight: '400px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        />
                        {msg.mediaMetadata?.caption && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                            {msg.mediaMetadata.caption}
                          </div>
                        )}
                      </div>
                    ) : msg.mediaType === 'video' && msg.mediaFilename ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <MessageVideo
                          conversationId={conversationId}
                          msg={msg}
                          style={{
                            maxWidth: '300px',
                            maxHeight: '400px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        >
                          Tu navegador no soporta el elemento de video.
                        </MessageVideo>
                        {msg.mediaMetadata?.caption && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                            {msg.mediaMetadata.caption}
                          </div>
                        )}
                      </div>
                    ) : msg.mediaType === 'audio' && msg.mediaFilename ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <MessageAudio
                          conversationId={conversationId}
                          msg={msg}
                          style={{
                            width: '100%',
                            maxWidth: '300px'
                          }}
                        >
                          Tu navegador no soporta el elemento de audio.
                        </MessageAudio>
                      </div>
                    ) : msg.mediaType === 'document' && msg.mediaFilename ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <MessageDocumentLink
                          conversationId={conversationId}
                          msg={msg}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.75rem',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            textDecoration: 'none',
                            color: '#212529',
                            maxWidth: '300px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        >
                          <svg
                            style={{ width: '32px', height: '32px', color: '#0d6efd', flexShrink: 0 }}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                          </svg>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.9rem', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {msg.mediaMetadata?.filename || 'Documento'}
                            </div>
                            {msg.mediaMetadata?.size && (
                              <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                {(msg.mediaMetadata.size / 1024).toFixed(1)} KB
                              </div>
                            )}
                          </div>
                        </MessageDocumentLink>
                        {msg.mediaMetadata?.caption && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                            {msg.mediaMetadata.caption}
                          </div>
                        )}
                      </div>
                    ) : msg.mediaType === 'sticker' && msg.mediaFilename ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <MessageImage
                          conversationId={conversationId}
                          msg={msg}
                          alt="Sticker"
                          clickable={false}
                          style={{
                            maxWidth: '150px',
                            maxHeight: '150px'
                          }}
                        />
                      </div>
                    ) : msg.mediaType === 'location' && msg.mediaMetadata ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <div
                          style={{
                            padding: '0.75rem',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            maxWidth: '300px'
                          }}
                        >
                          <div style={{ fontSize: '0.9rem', fontWeight: '500', marginBottom: '0.25rem' }}>
                            📍 {msg.mediaMetadata.name || 'Ubicación'}
                          </div>
                          {msg.mediaMetadata.address && (
                            <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                              {msg.mediaMetadata.address}
                            </div>
                          )}
                          <a
                            href={`https://www.google.com/maps?q=${msg.mediaMetadata.latitude},${msg.mediaMetadata.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '0.85rem',
                              color: '#0d6efd',
                              textDecoration: 'none'
                            }}
                          >
                            Ver en Google Maps →
                          </a>
                        </div>
                      </div>
                    ) : msg.mediaType === 'contacts' && msg.mediaMetadata?.contacts ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        {msg.mediaMetadata.contacts.map((contact, idx) => (
                          <div
                            key={idx}
                            style={{
                              padding: '0.75rem',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '8px',
                              marginBottom: '0.5rem',
                              maxWidth: '300px'
                            }}
                          >
                            <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                              👤 {contact.name?.formatted_name || 'Contacto'}
                            </div>
                            {contact.phones?.[0] && (
                              <div style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '0.25rem' }}>
                                📱 {contact.phones[0].phone}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : msg.mediaFilename ? (
                      // Fallback para mediaFilename antiguo sin mediaType
                      <>
                        {msg.body.includes('🖼️') || msg.mediaFilename.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                          <>
                            <MessageImage
                              conversationId={conversationId}
                              msg={msg}
                              alt={t('conversation.message.imageSent')}
                              style={{
                                maxWidth: '300px',
                                marginTop: '0.5rem',
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                            />
                            {msg.body && !msg.body.includes('🖼️') && (
                              <div style={{ ...styles.messageBody, marginTop: '0.5rem' }} title={msg.body}>{msg.body}</div>
                            )}
                          </>
                        ) : (
                          <>
                            <MessageDocumentLink
                              conversationId={conversationId}
                              msg={msg}
                              filename={msg.body.replace('📎 ', '')}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.75rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                textDecoration: 'none',
                                color: '#212529',
                                marginTop: '0.5rem',
                                maxWidth: '300px'
                              }}
                            >
                              <svg
                                style={{ width: '32px', height: '32px', color: '#0d6efd' }}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                />
                              </svg>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.9rem', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {msg.body.replace('📎 ', '')}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                  {t('conversation.message.download')}
                                </div>
                              </div>
                            </MessageDocumentLink>
                          </>
                        )}
                      </>
                    ) : (
                      <div style={styles.messageBody} title={msg.body}>{msg.body}</div>
                    )}
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          <form style={{ ...styles.form, position: 'relative' }} onSubmit={handleSend}>
            {/* Quick Reply Picker - aparece al escribir "/" */}
            <QuickReplyPicker
              market={conversation?.market}
              inputValue={newMessage}
              onSelect={handleQuickReplySelect}
              textareaRef={textareaRef}
            />

            {!hasMessages() && (
              <div style={styles.warningBanner}>
                ⚠️ No puedes enviar mensajes hasta que el cliente inicie la conversación
              </div>
            )}
            {hasMessages() && isConversationInactive() && (
              <div style={styles.warningBanner}>
                ⚠️ {t('conversation.message.inactive24h')}
              </div>
            )}

            {!canUserInteract() && (
              <div style={{ ...styles.warningBanner, backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404' }}>
                🔒 Esta conversación está asignada a otro agente. Solo puedes leer los mensajes.
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
              {/* Document uploader button */}
              <DocumentUploader conversationId={conversationId} disabled={!canSendMessage()} />

              {/* Voice note recorder button */}
              <AudioRecorder conversationId={conversationId} disabled={!canSendMessage()} />

              <textarea
                ref={textareaRef}
                style={{ ...styles.textarea, flex: 1 }}
                placeholder={
                  !canUserInteract()
                    ? 'No puedes enviar mensajes en esta conversación'
                    : !hasMessages()
                    ? 'Esperando primer mensaje del cliente...'
                    : isConversationInactive()
                    ? t('conversation.message.templateOnly')
                    : t('conversation.message.placeholder')
                }
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={3}
                disabled={!canSendMessage() || sending}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <TemplatePicker conversationId={conversationId} disabled={!canUserInteract()} templates={templates} loading={templatesLoading} />
              <button
                type="submit"
                style={{
                  ...styles.sendButton,
                  ...(!canSendMessage() ? styles.disabledButton : {}),
                  margin: 0
                }}
                disabled={!canSendMessage() || sending}
              >
                {sending ? t('conversation.message.sending') : t('common.send')}
              </button>
            </div>
          </form>
        </div>

        {/* Panel derecho: acciones */}
        <div style={styles.rightPanel} className="conversation-right-panel">
          <h3 style={styles.actionsTitle}>{t('conversation.actions.title')}</h3>

          {!canUserInteract() && (
            <div style={{ padding: '0.75rem', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem', color: '#856404' }}>
              🔒 Solo el owner de la conversación puede realizar acciones
            </div>
          )}

          {/* Lead Details Section */}
          <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
            <h4 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', fontWeight: '600', color: '#495057' }}>
              Detalles del Contacto
            </h4>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', color: '#6c757d' }}>
                Lead ID
              </label>
              <input
                type="text"
                value={leadId}
                onChange={(e) => setLeadId(e.target.value)}
                disabled={!!conversation?.leadId || !canUserInteract()}
                placeholder={conversation?.leadId ? '' : 'Ingrese Lead ID'}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ced4da',
                  fontSize: '0.85rem',
                  backgroundColor: (conversation?.leadId || !canUserInteract()) ? '#e9ecef' : 'white',
                  cursor: (conversation?.leadId || !canUserInteract()) ? 'not-allowed' : 'text',
                  color: conversation?.leadId ? '#495057' : '#212529'
                }}
              />
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', color: '#6c757d' }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!!conversation?.email || !canUserInteract()}
                placeholder={conversation?.email ? '' : 'Ingrese email'}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ced4da',
                  fontSize: '0.85rem',
                  backgroundColor: (conversation?.email || !canUserInteract()) ? '#e9ecef' : 'white',
                  cursor: (conversation?.email || !canUserInteract()) ? 'not-allowed' : 'text',
                  color: conversation?.email ? '#495057' : '#212529'
                }}
              />
            </div>

            {detailsError && (
              <div style={{ padding: '0.5rem', backgroundColor: '#f8d7da', borderRadius: '4px', marginBottom: '0.75rem', fontSize: '0.8rem', color: '#721c24' }}>
                {detailsError}
              </div>
            )}

            {(!conversation?.leadId || !conversation?.email) && canUserInteract() && (
              <button
                onClick={handleSaveDetails}
                disabled={savingDetails || (!leadId.trim() && !email.trim())}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  backgroundColor: savingDetails ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  cursor: savingDetails ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  opacity: (!leadId.trim() && !email.trim()) ? 0.6 : 1
                }}
              >
                {savingDetails ? 'Guardando...' : 'Guardar Detalles'}
              </button>
            )}
          </div>

          {/* Lead Status Section from Kitt */}
          {(leadStatus) && (
            <div className="lead-info-section" style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#495057' }}>
                  Información del Lead
                </h4>
                <button
                  onClick={fetchLeadStatus}
                  disabled={loadingLeadStatus}
                  style={{
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.75rem',
                    backgroundColor: '#423ef4',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loadingLeadStatus ? 'not-allowed' : 'pointer',
                    opacity: loadingLeadStatus ? 0.6 : 1
                  }}
                >
                  {loadingLeadStatus ? '⟳' : '↻'} Actualizar
                </button>
              </div>

              {loadingLeadStatus && (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#6c757d', fontSize: '0.85rem' }}>
                  Cargando información del lead...
                </div>
              )}

              {leadStatusError && (
                <div style={{ padding: '0.5rem', backgroundColor: '#f8d7da', borderRadius: '4px', marginBottom: '0.75rem', fontSize: '0.8rem', color: '#721c24' }}>
                  {leadStatusError}
                </div>
              )}

              {!loadingLeadStatus && !leadStatusError && leadStatus && (
                <div style={{ fontSize: '0.85rem' }}>
                  {leadStatus.name && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Nombre:</span>
                      <span style={{ color: '#212529' }}>{leadStatus.name}</span>
                    </div>
                  )}
                  {leadStatus.lastName && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Apellido:</span>
                      <span style={{ color: '#212529' }}>{leadStatus.lastName}</span>
                    </div>
                  )}
                  {leadStatus.email && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Email:</span>
                      <span style={{ color: '#212529', fontSize: '0.8rem' }}>{leadStatus.email}</span>
                    </div>
                  )}
                  {leadStatus.phone && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Teléfono:</span>
                      <span style={{ color: '#212529' }}>{leadStatus.phone}</span>
                    </div>
                  )}
                  {leadStatus.idNumber && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>DNI/NIE:</span>
                      <span style={{ color: '#212529' }}>{leadStatus.idNumber}</span>
                    </div>
                  )}
                  {leadStatus.gender && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Género:</span>
                      <span style={{ color: '#212529' }}>{leadStatus.gender === 'M' ? 'Masculino' : leadStatus.gender === 'F' ? 'Femenino' : leadStatus.gender}</span>
                    </div>
                  )}
                  {leadStatus.nationality && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Nacionalidad:</span>
                      <span style={{ color: '#212529' }}>{leadStatus.nationality}</span>
                    </div>
                  )}
                  {leadStatus.status && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Estado:</span>
                      <span style={{
                        color: 'white',
                        backgroundColor: leadStatus.status === 'active' ? '#28a745' : leadStatus.status === 'converted' ? '#007bff' : '#6c757d',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        {leadStatus.status}
                      </span>
                    </div>
                  )}
                  {leadStatus.market && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Mercado:</span>
                      <span style={{ color: '#212529', textTransform: 'uppercase' }}>{leadStatus.market}</span>
                    </div>
                  )}
                  {leadStatus.purchaseCapital && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Capital:</span>
                      <span style={{ color: '#212529', fontWeight: '600' }}>{leadStatus.purchaseCapital}€</span>
                    </div>
                  )}
                  {leadStatus.price && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Precio:</span>
                      <span style={{ color: '#28a745', fontWeight: '600' }}>{leadStatus.price}€</span>
                    </div>
                  )}
                  {leadStatus.periodicity && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Periodicidad:</span>
                      <span style={{ color: '#212529' }}>{leadStatus.periodicity}</span>
                    </div>
                  )}
                  {leadStatus.effect_date && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Fecha efecto:</span>
                      <span style={{ color: '#212529' }}>{new Date(leadStatus.effect_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {leadStatus.policyNumber && (
                    <div className="lead-info-row" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e9ecef', paddingBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#6c757d' }}>Póliza:</span>
                      <span style={{ color: '#007bff', fontWeight: '600' }}>{leadStatus.policyNumber}</span>
                    </div>
                  )}
                </div>
              )}

              {!loadingLeadStatus && !leadStatusError && !leadStatus && (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#6c757d', fontSize: '0.85rem' }}>
                  No hay información disponible del lead
                </div>
              )}
            </div>
          )}

          {/* Document Actions Section */}
          {leadStatus && canSendMessage() && (
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: '600', color: '#495057' }}>
                Enviar Documentos
              </h4>

              <button
                onClick={() => handleSendDocument('contract')}
                disabled={!canSendContract() || sendingContract}
                style={{
                  ...styles.documentBtn,
                  ...((!canSendContract() || sendingContract) ? styles.documentBtnDisabled : {})
                }}
                title={!canSendContract() ? 'Requiere status "complete"' : 'Enviar contrato al cliente'}
              >
                {sendingContract ? '⏳ Enviando...' : '📄 Enviar Contrato'}
              </button>

              <button
                onClick={() => handleSendDocument('forecast')}
                disabled={!canSendForecast() || sendingForecast}
                style={{
                  ...styles.documentBtn,
                  ...((!canSendForecast() || sendingForecast) ? styles.documentBtnDisabled : {})
                }}
                title={!canSendForecast() ? 'No disponible para este status' : 'Enviar forecast al cliente'}
              >
                {sendingForecast ? '⏳ Enviando...' : '📊 Enviar Forecast'}
              </button>

              <button
                onClick={() => handleSendDocument('promo-conditions')}
                disabled={!canSendPromoConditions() || sendingPromo}
                style={{
                  ...styles.documentBtn,
                  ...((!canSendPromoConditions() || sendingPromo) ? styles.documentBtnDisabled : {})
                }}
                title="Enviar condiciones de la promoción al cliente"
              >
                {sendingPromo ? '⏳ Enviando...' : '🎁 Enviar Condiciones Promo'}
              </button>
            </div>
          )}

          {conversation?.status === 'agent_assigned' ? (
            <button
              style={{
                ...styles.automateBtn,
                ...(canUserInteract() ? {} : { opacity: 0.5, cursor: 'not-allowed' })
              }}
              onClick={canUserInteract() ? handleAutomate : undefined}
              disabled={!canUserInteract()}
            >
              {t('conversation.actions.markAutomatic')}
            </button>
          ) : (
            <p style={styles.noActionText}>
              {conversation ? t('conversation.actions.inAutomatic') : ''}
            </p>
          )}
          {(conversation?.leadId || conversation?.email) && (
            <button
              style={{
                ...styles.automateBtn,
                ...(canUserInteract() ? {} : { opacity: 0.5, cursor: 'not-allowed' })
              }}
              onClick={canUserInteract() ? () => sendOTP() : undefined}
              disabled={!canUserInteract()}
            >
              {t('conversation.actions.sendOtp')}
            </button>
          )}

          {/* Selector de respuestas rápidas */}
          <QuickReplySelector
            market={conversation?.market}
            onSelect={handleQuickReplySelect}
            disabled={!canUserInteract()}
          />

          {/* Selector de plantillas */}
          <TemplateSelector conversationId={conversationId} disabled={!canUserInteract()} templates={templates} loading={templatesLoading} />
          </div>
        </div>

        {/* Modales flotantes */}
        {(loading || loadingConv) && (
          <div style={styles.modal}>
            <p>{t('common.loading')}</p>
          </div>
        )}
        {error && (
          <div style={{ ...styles.modal, borderColor: '#f5222d' }}>
            <p style={{ margin: 0, color: '#f5222d' }}>{t('common.error')}: {error}</p>
          </div>
        )}
        {success && (
          <div style={{ ...styles.modal, borderColor: '#22f581' }}>
            <p style={{ margin: 0, color: '22f581' }}>{success}</p>
          </div>
        )}

        {/* Modal de Notas */}
      </div>
      {createPortal(modal, document.body)}

      {/* Modal de Reasignación */}
      <ReassignModal
        isOpen={reassignModalOpen}
        onClose={() => setReassignModalOpen(false)}
        onReassign={handleReassign}
        currentAgentId={conversation?.agentId}
      />

      {/* Modal de Bloqueo */}
      <BlockContactModal
        isOpen={blockModalOpen}
        onClose={() => setBlockModalOpen(false)}
        onBlock={handleBlockContact}
        whatsappId={conversation?.whatsappId || ''}
      />

      {/* Modal de Confirmación de Eliminación */}
      {deleteModalOpen && createPortal(
        <div style={styles.modalOverlay} onClick={() => setDeleteModalOpen(false)}>
          <div style={styles.deleteModalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.deleteModalTitle}>⚠️ Eliminar conversación</h2>
            <p style={styles.deleteModalText}>
              ¿Estás seguro de que deseas eliminar esta conversación?
            </p>
            <p style={styles.deleteModalWarning}>
              Esta acción eliminará permanentemente todos los mensajes y no se puede deshacer.
            </p>
            <div style={styles.deleteModalButtons}>
              <button
                style={styles.cancelButton}
                onClick={() => setDeleteModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                style={styles.confirmDeleteButton}
                onClick={handleDeleteConversation}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100vw',
    margin: 0,
    padding: 0,
    backgroundColor: '#fafafa',
    position: 'relative',
  },
  topNavbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1.5rem',
    backgroundColor: '#ffffff',
    borderBottom: '2px solid #e8e8e8',
    flexShrink: 0,
  },
  navbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  navbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  navbarTitle: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#2c3e50',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  closeBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  reopenBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1890ff',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  optionsMenuContainer: {
    position: 'relative',
  },
  optionsMenuBtn: {
    width: '36px',
    height: '36px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '1.5rem',
    borderRadius: '6px',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#2c3e50',
  },
  optionsDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '0.5rem',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    minWidth: '200px',
    zIndex: 1000,
    overflow: 'hidden',
  },
  optionItem: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '0.9rem',
    textAlign: 'left',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#2c3e50',
  },
  mainContent: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
    position: 'relative',
  },
  leftPanel: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: '1rem',
    overflow: 'hidden',
  },
  rightPanel: {
    width: '300px',
    minWidth: '280px',
    maxWidth: '350px',
    backgroundColor: '#f5f5f5',
    borderLeft: '1px solid #ddd',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  backBtn: {
    padding: '0.4rem 0.8rem',
    fontSize: '0.9rem',
    cursor: 'pointer',
    borderRadius: '6px',
    border: '1px solid #d9d9d9',
    backgroundColor: '#ffffff',
    color: '#2c3e50',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  title: {
    textAlign: 'center',
    margin: '0 0 1rem 0',
    fontSize: '1.25rem',
  },
  badgeAgent: {
    marginLeft: '0.5rem',
    backgroundColor: '#faad14',
    color: '#fff',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '0.75rem',
  },
  badgeAuto: {
    marginLeft: '0.5rem',
    backgroundColor: '#52c41a',
    color: '#fff',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '0.75rem',
  },
  messagesContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    padding: '0.5rem',
    borderTop: '1px solid #eee',
    borderBottom: '1px solid #eee',
    overflowY: 'auto',
    backgroundColor: '#fafafa',
  },
  noMessagesText: {
    color: '#666',
    textAlign: 'center',
    marginTop: '2rem',
    fontSize: '1rem',
  },
  messageBubble: {
    maxWidth: '70%',
    padding: '0.75rem 1rem',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  messageBody: {
    display: '-webkit-box',
    WebkitLineClamp: 7,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'default',
    whiteSpace: 'pre-wrap',
  },
  messageHeader: {
    marginBottom: '0.25rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  senderLabel: {
    color: '#fff',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  timestamp: {
    fontSize: '0.75rem',
    color: '#555',
  },
  messageBody: {
    fontSize: '1rem',
    color: '#333',
    whiteSpace: 'pre-wrap',
  },
  form: {
    padding: '0.75rem 1rem',
    borderTop: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
    resize: 'none',
  },
  sendButton: {
    marginTop: '0.5rem',
    padding: '0.75rem',
    fontSize: '1rem',
    backgroundColor: '#423ef4',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    alignSelf: 'flex-end',
  },
  actionsTitle: {
    fontSize: '1.1rem',
    marginBottom: '1rem',
    textAlign: 'center',
  },
  automateBtn: {
    padding: '0.75rem',
    fontSize: '1rem',
    backgroundColor: '#423ef4',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginBottom: '0.5rem',
  },
  noActionText: {
    fontSize: '0.9rem',
    color: '#666',
    textAlign: 'center',
    marginTop: '1rem',
  },
  modal: {
    position: 'fixed',
    top: '1rem',
    right: '1rem',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1000,
  },
  warningBanner: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    border: '1px solid #ffc107',
    borderRadius: '4px',
    padding: '0.75rem',
    marginBottom: '0.5rem',
    fontSize: '0.9rem',
    textAlign: 'center',
  },
  disabledButton: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  deleteModalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '2rem',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  deleteModalTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1.5rem',
    color: '#dc2626',
  },
  deleteModalText: {
    margin: '0 0 0.5rem 0',
    fontSize: '1rem',
    color: '#374151',
  },
  deleteModalWarning: {
    margin: '0 0 1.5rem 0',
    fontSize: '0.9rem',
    color: '#ef4444',
    fontWeight: '500',
  },
  deleteModalButtons: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    padding: '0.5rem 1.5rem',
    fontSize: '0.9rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontWeight: '500',
  },
  confirmDeleteButton: {
    padding: '0.5rem 1.5rem',
    fontSize: '0.9rem',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: '#dc2626',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: '500',
  },
  documentBtn: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    fontSize: '0.85rem',
    backgroundColor: '#fff',
    color: '#495057',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    cursor: 'pointer',
    marginBottom: '0.5rem',
    fontWeight: '500',
    transition: 'all 0.2s',
    textAlign: 'left',
  },
  documentBtnDisabled: {
    backgroundColor: '#f8f9fa',
    color: '#adb5bd',
    cursor: 'not-allowed',
    opacity: 0.7,
  },
};
